import { getFlowEligibility } from 'services/dashboard.service';

import { clearPlayerInfoCache } from './playerInfoCache';
import { FlowEligibility, FlowKey } from './types';

const ELIGIBILITY_CACHE_KEY = 'apreciacao_flow_eligibility_cache';
// TTL curto: elegibilidade muda quando a base de players/tasks muda — 5 minutos evita
// repetir o aggregate a cada navegação sem congelar o acesso do gestor.
const ELIGIBILITY_CACHE_TTL_MS = 5 * 60 * 1000;

const FLOW_CHOICE_KEY = 'apreciacao_flow_choice';

// Raiz das rotas de apreciação — os fluxos vivem em `${APRECIACAO_ROOT}/<FlowKey>`.
export const APRECIACAO_ROOT = '/minha-equipe/validar-apreciar-realizacao';

// O fluxo do outro lado da troca, para quem precisa navegar "para o que não é este".
export const OTHER_FLOW: Record<FlowKey, FlowKey> = {
  'diretoria-rede': 'mad',
  mad: 'diretoria-rede',
};

interface EligibilityCacheEntry {
  eligibility: FlowEligibility;
  timestamp: number;
}

// A elegibilidade é POR CICLO (o mesmo gestor pode ter só MAD num semestre e os dois
// fluxos no seguinte), então o ciclo entra na chave — senão a resposta de um ciclo
// vazaria para o outro dentro do TTL. Sem ciclo informado, a chave marca o ciclo que o
// backend escolhe sozinho (o corrente).
function eligibilityCacheKey(player: string, cycleId?: string) {
  return `${ELIGIBILITY_CACHE_KEY}:${player}:${cycleId || 'corrente'}`;
}

function readEligibilityCache(player: string, cycleId?: string): FlowEligibility | null {
  try {
    const raw = window.sessionStorage.getItem(eligibilityCacheKey(player, cycleId));
    if (!raw) return null;

    const entry: EligibilityCacheEntry = JSON.parse(raw);
    if (!entry?.eligibility || typeof entry.timestamp !== 'number') return null;
    if (Date.now() - entry.timestamp > ELIGIBILITY_CACHE_TTL_MS) return null;

    return {
      normal: !!entry.eligibility.normal,
      mad: !!entry.eligibility.mad,
      // Preserva as pendências por fluxo no cache — sem isso a 2ª leitura dentro do TTL
      // perderia a contagem e o gate voltaria a decidir só pela escolha.
      normalPending: entry.eligibility.normalPending,
      madPending: entry.eligibility.madPending,
    };
  } catch (e) {
    return null;
  }
}

function writeEligibilityCache(player: string, eligibility: FlowEligibility, cycleId?: string) {
  try {
    const entry: EligibilityCacheEntry = { eligibility, timestamp: Date.now() };
    window.sessionStorage.setItem(eligibilityCacheKey(player, cycleId), JSON.stringify(entry));
  } catch (e) {
    // sessionStorage indisponível — segue sem cache
  }
}

// Resolve a elegibilidade de fluxo do gestor com cache de sessão por player e ciclo.
// Retorna null quando o player ainda não é conhecido ou quando o aggregate nega acesso —
// o "sem acesso" não é cacheado para não perpetuar um erro transitório de rede.
// `fresh: true` PULA a leitura do cache (mas grava o resultado): usado após recargas da
// lista para as pendências das abas refletirem as apreciações recém-feitas — e o
// cache atualizado também corrige o próximo redirect do gate.
// `cycleId` é o ciclo selecionado na página; omitido, o backend responde pelo corrente.
export async function resolveFlowEligibility(
  player?: string,
  options?: { fresh?: boolean; cycleId?: string },
): Promise<FlowEligibility | null> {
  if (!player) return null;

  const cycleId = options?.cycleId;

  if (!options?.fresh) {
    const cached = readEligibilityCache(player, cycleId);
    if (cached) return cached;
  }

  const eligibility = await getFlowEligibility(player, cycleId);
  if (!eligibility) return null;

  writeEligibilityCache(player, eligibility, cycleId);

  return eligibility;
}

// A escolha vive em sessionStorage DE PROPÓSITO: vale só para a navegação ativa —
// fechar o browser/aba a descarta sozinha, e o logout a limpa via clearFlowSessionState.
export function getFlowChoice(): FlowKey | null {
  try {
    const value = window.sessionStorage.getItem(FLOW_CHOICE_KEY);

    return value === 'diretoria-rede' || value === 'mad' ? value : null;
  } catch (e) {
    return null;
  }
}

export function setFlowChoice(flow: FlowKey) {
  try {
    window.sessionStorage.setItem(FLOW_CHOICE_KEY, flow);
  } catch (e) {
    // sessionStorage indisponível — a escolha não persiste, sem quebrar a navegação
  }
}

// Limpa o estado de sessão da apreciação (escolha de fluxo + caches de elegibilidade).
// Chamado no signOut/login (AuthContext): o logout MSAL mantém a aba viva, então o
// sessionStorage sobreviveria a uma troca de usuário sem esta limpeza explícita.
export function clearFlowSessionState() {
  clearPlayerInfoCache();

  try {
    window.sessionStorage.removeItem(FLOW_CHOICE_KEY);

    const staleKeys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(ELIGIBILITY_CACHE_KEY)) {
        staleKeys.push(key);
      }
    }
    staleKeys.forEach(key => window.sessionStorage.removeItem(key));
  } catch (e) {
    // sessionStorage indisponível — nada a limpar
  }
}

// Decide o fluxo de destino do guarda-chuva (ELE-02): elegível a um só fluxo vai direto
// a ele; a nenhum, null (o chamador exibe "sem acesso"). Elegível a AMBOS:
//   1. a ESCOLHA da sessão manda — quem estava no /mad volta ao /mad (os fluxos gravam a
//      visita; o switcher grava o clique). Pendência NÃO atropela navegação ativa;
//   2. sem escolha (primeira entrada da sessão), prefere o fluxo com pendência para não
//      abrir numa tela vazia (ELE-05); empate/indisponível cai em diretoria-rede.
export function resolveRedirect(eligibility: FlowEligibility | null, choice: FlowKey | null): FlowKey | null {
  if (!eligibility || (!eligibility.normal && !eligibility.mad)) return null;

  if (eligibility.normal && !eligibility.mad) return 'diretoria-rede';
  if (eligibility.mad && !eligibility.normal) return 'mad';

  if (choice === 'diretoria-rede' || choice === 'mad') return choice;

  const madPending = eligibility.madPending ?? 0;
  const normalPending = eligibility.normalPending ?? 0;

  if (madPending > 0 && normalPending === 0) return 'mad';
  if (normalPending > 0 && madPending === 0) return 'diretoria-rede';

  return 'diretoria-rede';
}
